// src/config/plans.ts

export type PlanKey = "free" | "basic" | "pro" | "premium";
export type ResolutionKey = "720p" | "1080p" | "2k" | "4k" | "8k";

/**
 * Brand Mode (Brand Studio) - piezas disponibles
 * Ajusta estas keys para que coincidan con tu UI / rutas internas.
 */
export type BrandPieceKey = "post_campaign" | "banner" | "ads" | "product_creative";

/**
 * ✅ Brand Mode capabilities (fuente de verdad)
 * Esto alimenta:
 * - candados en Brand Studio
 * - gating en BrandKit / BrandCreate
 * - UI de /upgrade
 */
export type BrandCapabilities = {
  /**
   * Persistencia real del kit (demo vs real)
   * - free: false (demo)
   * - basic+: true
   */
  canPersistBrandKit: boolean;

  /**
   * Si BrandCreate puede aplicar el Brand en generación (usar Brand Kit)
   * - free: false (demo)
   * - basic+: true
   */
  canUseBrandInGeneration: boolean;

  /**
   * Campos avanzados (mood/referencia/guías)
   * - free/basic: false
   * - pro+: true
   */
  canUseAdvancedBrandFields: boolean;

  /**
   * Límite de brands activas
   * - free/basic: 1
   * - pro: 3
   * - premium: "ilimitadas" (límite alto)
   */
  brandKitsLimit: number;

  /**
   * Piezas disponibles en Brand Studio
   */
  allowedPieces: BrandPieceKey[];
};

/**
 * Etiquetas bonitas para UI
 */
export const RESOLUTION_LABELS: Record<ResolutionKey, string> = {
  "720p": "720p · Low",
  "1080p": "1080p · Medium",
  "2k": "2K · High",
  "4k": "4K · Ultra",
  "8k": "8K · Pro",
};

/**
 * COSTO BASE EN CRÉDITOS POR IMAGEN
 * OJO: el backend debe usar ESTO como fuente de verdad del costo.
 */
export const RESOLUTION_CREDIT_COST: Record<ResolutionKey, number> = {
  "720p": 1,
  "1080p": 3,
  "2k": 6,
  "4k": 10,
  "8k": 25,
};

export type PlanConfig = {
  id: PlanKey;
  name: string;
  priceMonthlyUSD: number; // informativo por ahora
  creditsMonthly: number;

  // Creator Mode / calidad
  allowedResolutions: ResolutionKey[];
  resolutionMultipliers: Partial<Record<ResolutionKey, number>>;

  // Brand Mode / capabilities
  brand: BrandCapabilities;
};

/**
 * Orden de planes (importante para "required plan" y UX de upgrade)
 */
export const PLAN_ORDER: PlanKey[] = ["free", "basic", "pro", "premium"];

/**
 * ✅ Para "ilimitadas" sin Infinity (más estable para UI y storage)
 */
export const BRAND_LIMIT_UNLIMITED = 999999;

/**
 * Labels bonitos de piezas (UI)
 */
export const BRAND_PIECE_LABELS: Record<BrandPieceKey, string> = {
  post_campaign: "Post para campaña",
  banner: "Banner",
  ads: "Anuncio (Ads)",
  product_creative: "Creativo de producto",
};

/**
 * PLANES
 */
export const PLANS: Record<PlanKey, PlanConfig> = {
  free: {
    id: "free",
    name: "Free",
    priceMonthlyUSD: 0,
    creditsMonthly: 30,

    // ✅ FREE = límite claro: 720p y créditos limitados
    allowedResolutions: ["720p"],
    resolutionMultipliers: { "720p": 1.0 },

    brand: {
      // ✅ DEMO
      canPersistBrandKit: false,
      canUseBrandInGeneration: false,
      canUseAdvancedBrandFields: false,

      brandKitsLimit: 1,

      // ✅ Mantén tu gating actual
      allowedPieces: ["post_campaign"],
    },
  },

  basic: {
    id: "basic",
    name: "Basic",
    priceMonthlyUSD: 9,
    creditsMonthly: 300,

    allowedResolutions: ["720p", "1080p"],
    resolutionMultipliers: { "720p": 1.0, "1080p": 1.0 },

    brand: {
      // ✅ 1 brand + persistencia base + usar brand en generación
      canPersistBrandKit: true,
      canUseBrandInGeneration: true,
      canUseAdvancedBrandFields: false,

      brandKitsLimit: 1,

      allowedPieces: ["post_campaign", "banner"],
    },
  },

  pro: {
    id: "pro",
    name: "Pro",
    priceMonthlyUSD: 19,
    creditsMonthly: 700,

    allowedResolutions: ["720p", "1080p", "2k", "4k"],
    resolutionMultipliers: { "720p": 1.0, "1080p": 1.0, "2k": 1.0, "4k": 1.0 },

    brand: {
      // ✅ hasta 3 brands + advanced
      canPersistBrandKit: true,
      canUseBrandInGeneration: true,
      canUseAdvancedBrandFields: true,

      brandKitsLimit: 3,

      allowedPieces: ["post_campaign", "banner", "ads", "product_creative"],
    },
  },

  premium: {
    id: "premium",
    name: "Premium",
    priceMonthlyUSD: 39,
    creditsMonthly: 1200,

    allowedResolutions: ["720p", "1080p", "2k", "4k", "8k"],
    resolutionMultipliers: { "720p": 1.0, "1080p": 1.0, "2k": 1.0, "4k": 1.0, "8k": 1.0 },

    brand: {
      // ✅ “ilimitadas” (límite alto)
      canPersistBrandKit: true,
      canUseBrandInGeneration: true,
      canUseAdvancedBrandFields: true,

      brandKitsLimit: BRAND_LIMIT_UNLIMITED,

      allowedPieces: ["post_campaign", "banner", "ads", "product_creative"],
    },
  },
};

/**
 * Helpers (planes)
 */
export function getPlanConfig(plan: PlanKey): PlanConfig {
  return PLANS[plan];
}

export function comparePlan(a: PlanKey, b: PlanKey): number {
  return PLAN_ORDER.indexOf(a) - PLAN_ORDER.indexOf(b);
}

export function hasAtLeastPlan(current: PlanKey, required: PlanKey): boolean {
  return comparePlan(current, required) >= 0;
}

/**
 * Helpers (resolución)
 */
export function isResolutionAllowed(plan: PlanKey, resolution: ResolutionKey): boolean {
  return PLANS[plan].allowedResolutions.includes(resolution);
}

export function costCreditsForResolution(plan: PlanKey, resolution: ResolutionKey): number {
  const base = RESOLUTION_CREDIT_COST[resolution];
  const mult = PLANS[plan].resolutionMultipliers[resolution] ?? 1.0;
  return Math.ceil(base * mult);
}

export function getMinPlanForResolution(resolution: ResolutionKey): PlanKey {
  for (const plan of PLAN_ORDER) {
    if (isResolutionAllowed(plan, resolution)) return plan;
  }
  return "premium";
}

/**
 * Brand Mode helpers (piezas + límites + capabilities)
 */
export function isBrandPieceAllowed(plan: PlanKey, piece: BrandPieceKey): boolean {
  return PLANS[plan].brand.allowedPieces.includes(piece);
}

export function getMinPlanForBrandPiece(piece: BrandPieceKey): PlanKey {
  for (const plan of PLAN_ORDER) {
    if (isBrandPieceAllowed(plan, piece)) return plan;
  }
  return "premium";
}

export function canPersistBrandKit(plan: PlanKey): boolean {
  return PLANS[plan].brand.canPersistBrandKit;
}

export function canUseBrandInGeneration(plan: PlanKey): boolean {
  return PLANS[plan].brand.canUseBrandInGeneration;
}

export function canUseAdvancedBrandFields(plan: PlanKey): boolean {
  return PLANS[plan].brand.canUseAdvancedBrandFields;
}

export function getBrandKitsLimit(plan: PlanKey): number {
  return PLANS[plan].brand.brandKitsLimit;
}

export function isUnlimitedBrandLimit(plan: PlanKey): boolean {
  return getBrandKitsLimit(plan) >= BRAND_LIMIT_UNLIMITED;
}

/**
 * ✅ Pretty label de piezas (para Upgrade / UI)
 */
export function prettyBrandPiece(piece: BrandPieceKey | string): string {
  const key = String(piece || "").toLowerCase() as BrandPieceKey;
  return BRAND_PIECE_LABELS[key] ?? (piece || "Pieza");
}

/**
 * ✅ Plan mínimo requerido para:
 * - guardar / persistir brand kit
 * - usar brand en generación
 * - campos avanzados
 * - múltiples brands
 */
export type BrandFeatureKey =
  | "brand_persist"
  | "brand_use_in_generation"
  | "brand_advanced_fields"
  | "brand_multiple";

export function getMinPlanForBrandFeature(feature: BrandFeatureKey): PlanKey {
  for (const plan of PLAN_ORDER) {
    const b = PLANS[plan].brand;

    if (feature === "brand_persist" && b.canPersistBrandKit) return plan;
    if (feature === "brand_use_in_generation" && b.canUseBrandInGeneration) return plan;
    if (feature === "brand_advanced_fields" && b.canUseAdvancedBrandFields) return plan;
    if (feature === "brand_multiple" && b.brandKitsLimit > 1) return plan;
  }
  return "premium";
}

